import {
  PrinterDuty,
  PrinterTemplate,
  UsbWindowsPrinterConfig,
  UsbWindowsPrintersConfig,
} from '../types';
import { normalizePaperWidth } from '../paperWidth';
import { encodeTemplateToEscPos } from '../escpos/encodeTemplateToEscPos';
import { readPrinterSetting, writePrinterSetting } from '../printerSettingsStorage';

export interface WindowsInstalledPrinter {
  name: string;
  displayName: string;
  description: string;
  status: number;
  isDefault: boolean;
}

const normalizeUsbWindowsPrinterConfig = (
  printer: Partial<UsbWindowsPrinterConfig>
): UsbWindowsPrinterConfig => {
  return {
    id: printer.id ?? crypto.randomUUID(),
    name: printer.name ?? 'Windows USB Printer',
    duties: printer.duties ?? ['cashier'],
    printerName: printer.printerName ?? '',
    codePage: printer.codePage ?? 'cp858',
    paperWidth: normalizePaperWidth(printer.paperWidth),
    openCashDrawer: printer.openCashDrawer ?? false,
    printDispatchCopy: printer.printDispatchCopy ?? false,
    language: printer.language ?? 'es',
    currency: printer.currency ?? 'USD',
  };
};

export const createDefaultUsbWindowsPrinterConfig = (
  overrides: Partial<UsbWindowsPrinterConfig> = {}
): UsbWindowsPrinterConfig => {
  return normalizeUsbWindowsPrinterConfig({
    id: crypto.randomUUID(),
    duties: ['cashier'],
    ...overrides,
  });
};

export const getUsbWindowsPrintersConfig = (): UsbWindowsPrintersConfig => {
  try {
    const dbConfig = readPrinterSetting<UsbWindowsPrintersConfig>('usbWindowsPrintersConfig');
    if (dbConfig) {
      return {
        enabled: dbConfig.enabled ?? false,
        printers: (dbConfig.printers ?? []).map(normalizeUsbWindowsPrinterConfig),
      };
    }
  } catch (error) {
    console.warn('Failed to parse USB Windows printer config:', error);
  }

  return { enabled: false, printers: [] };
};

export const saveUsbWindowsPrintersConfig = async (
  config: UsbWindowsPrintersConfig
): Promise<void> => {
  await writePrinterSetting('usbWindowsPrintersConfig', config);
};

const getPrintersForDuty = (duty: PrinterDuty): UsbWindowsPrinterConfig[] => {
  const config = getUsbWindowsPrintersConfig();
  if (!config.enabled || config.printers.length === 0) return [];
  return config.printers.filter((printer) => printer.duties.includes(duty));
};

export const listWindowsInstalledPrinters = async (): Promise<WindowsInstalledPrinter[]> => {
  if (!window.electronAPI?.listWindowsPrinters) {
    throw new Error('Windows printer listing is only available in the Tabstr Windows app');
  }
  return window.electronAPI.listWindowsPrinters();
};

export const sendToUsbWindowsPrinter = async (
  template: PrinterTemplate,
  printerId: string,
  options?: { openCashDrawer?: boolean },
): Promise<boolean> => {
  const config = getUsbWindowsPrintersConfig();
  if (!config.enabled) return false;

  const printer = config.printers.find((item) => item.id === printerId);
  if (!printer) {
    throw new Error(`USB Windows printer not found: ${printerId}`);
  }
  if (!printer.printerName.trim()) {
    throw new Error(`Windows printer name is not configured for: ${printer.name}`);
  }
  if (!window.electronAPI?.printEscposWindowsRaw) {
    throw new Error('Windows USB printing is only available in the Tabstr Windows app');
  }

  const bytes = encodeTemplateToEscPos(template, {
    openCashDrawer: options?.openCashDrawer ?? printer.openCashDrawer,
    codePage: printer.codePage,
  });

  return window.electronAPI.printEscposWindowsRaw({
    printerName: printer.printerName,
    payload: Array.from(bytes),
  });
};

export const sendToUsbWindowsPrinterByDuty = async (
  template: PrinterTemplate,
  duty: PrinterDuty
): Promise<boolean> => {
  const printers = getPrintersForDuty(duty);
  if (printers.length === 0) {
    console.warn(`No USB Windows printer assigned for duty: ${duty}`);
    return false;
  }

  const results = await Promise.all(
    printers.map((printer) => {
      console.log(
        `[USB Windows] Sending document to printer: ${printer.name} (duty: ${duty})`
      );
      return sendToUsbWindowsPrinter(template, printer.id);
    })
  );

  return results.some((success) => success);
};
